import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export const DEFAULT_PER_USE_ACTIVATION_FEE = 2000;

export interface PerUseCardPlan {
  billing_mode?: string | null;
  payment_mode?: string | null;
}

export function requiresPerUseCard(plan: PerUseCardPlan | null | undefined): boolean {
  return plan?.billing_mode === "per_use" && plan?.payment_mode !== "cash";
}

export function usePerUseActivationFee(enabled: boolean): number {
  const [activationFee, setActivationFee] = useState(DEFAULT_PER_USE_ACTIVATION_FEE);

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    (async () => {
      const { data } = await (supabase as any)
        .from("platform_settings")
        .select("value")
        .eq("key", "per_use_activation_fee")
        .eq("brand", "flow")
        .maybeSingle();
      const fee = Number(data?.value);
      if (mounted && fee > 0) setActivationFee(fee);
    })();
    return () => { mounted = false; };
  }, [enabled]);

  return activationFee;
}

export async function fetchPerUseCardReady(orgId: string): Promise<boolean | null> {
  const [card, awaiting] = await Promise.all([
    (supabase as any)
      .from("mp_saved_cards")
      .select("id")
      .eq("organization_id", orgId)
      .eq("is_active", true)
      .maybeSingle(),
    (supabase as any).rpc("organization_awaits_card_authorization", { p_org_id: orgId }),
  ]);
  if (card.error) return null;
  return !!card.data && awaiting.data !== true;
}

export function usePerUseCardStatus(orgId: string | null, enabled: boolean): boolean | null {
  const [hasSavedCard, setHasSavedCard] = useState<boolean | null>(null);

  useEffect(() => {
    setHasSavedCard(null);
    if (!orgId || !enabled) return;
    let mounted = true;
    (async () => {
      const ready = await fetchPerUseCardReady(orgId);
      if (mounted && ready !== null) setHasSavedCard(ready);
    })();
    return () => { mounted = false; };
  }, [orgId, enabled]);

  return hasSavedCard;
}

export async function fetchAwaitsCardAuthorization(orgId: string): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.rpc("organization_awaits_card_authorization" as never, { p_org_id: orgId } as never);
    if (error || typeof data !== "boolean") return null;
    return data;
  } catch {
    return null;
  }
}

export function usePerUseAwaitsAuthorization(
  orgId: string | null,
  enabled: boolean,
  reload = 0,
): { awaits: boolean | null; loading: boolean } {
  const key = orgId && enabled ? `${orgId}:${reload}` : null;
  const [result, setResult] = useState<{ key: string; awaits: boolean | null } | null>(null);

  useEffect(() => {
    if (!key || !orgId) return;
    let mounted = true;
    void fetchAwaitsCardAuthorization(orgId).then((awaits) => {
      if (mounted) setResult({ key, awaits });
    });
    return () => { mounted = false; };
  }, [key, orgId]);

  if (!key) return { awaits: null, loading: false };
  if (result?.key !== key) return { awaits: null, loading: true };
  return { awaits: result.awaits, loading: false };
}
